import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalInvestigationsDebarredItemModel } from 'forms/sections/legal/investigation/debarred/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import { LegalInvestigationsInvestigatingAgencyModel } from 'forms/sections/legal/investigation/history/types';
import NotApplicable from 'components/Form/NotApplicable';
import DateRange from 'components/Form/DateRange';
import InvestigatingAgency from './InvestigatingAgency';

interface Props extends Partial<LegalInvestigationsDebarredItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
}

export default class DebarredItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalInvestigationsDebarredItemModel>) => {
    this.props.onUpdate({
      Agency: this.props.Agency,
      AgencyExplanation: this.props.AgencyExplanation,
      AgencyNotApplicable: this.props.AgencyNotApplicable,
      Date: this.props.Date,
      DateRange: this.props.DateRange,
      Explanation: this.props.Explanation,
      ...queue,
    });
  };

  updateAgency = (values: LegalInvestigationsDebarredItemModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateAgencyPvq = (values: Partial<LegalInvestigationsDebarredItemModel>) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Agency?.value === 'Other' ? this.props.AgencyExplanation : {} as LegalInvestigationsDebarredItemModel['AgencyExplanation'],
    });
  };

  updateAgencyExplanation = (value: LegalInvestigationsDebarredItemModel['AgencyExplanation']) => {
    this.update({
      AgencyExplanation: value
    });
  };

  updateAgencyNotApplicable = (
    values: LegalInvestigationsDebarredItemModel['AgencyNotApplicable']
  ) => {
    this.update({
      AgencyNotApplicable: values,
      Agency: {} as LegalInvestigationsDebarredItemModel['Agency'],
      AgencyExplanation: {} as LegalInvestigationsDebarredItemModel['AgencyExplanation'],
    });
  };

  updateDate = (values: LegalInvestigationsDebarredItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDateRange = (values: LegalInvestigationsDebarredItemModel['DateRange']) => {
    this.update({
      DateRange: values,
    });
  };

  updateExplanation = (values: LegalInvestigationsDebarredItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t('legal.pvq.investigations.debarred.heading.agency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.AgencyNotApplicable}
            name="AgencyNotApplicable"
            className="legal-investigations-revoked-agency-notapplicable"
            onUpdate={this.updateAgencyNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.pvq.investigations.debarred.para.or')}
            label={i18n.t('legal.pvq.investigations.debarred.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.AgencyNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Agency?.value || this.props.AgencyExplanation?.value)}
          >
            <InvestigatingAgency
              {...{
                Agency: this.props.Agency,
                Explanation: this.props.AgencyExplanation,
              }}
              name="Agency"
              onUpdate={this.updateAgencyPvq}
              onError={this.props.onError}
              className="legal-investigations-history-agency"
              required={this.props.required && this.props.AgencyNotApplicable?.applicable}
              scrollIntoView={this.props.scrollIntoView}
              attribute={attribute}
              formType={this.props.formType}
            />
          </NotApplicable>
        </Field>

        {this.props.Agency?.value === 'Other'
          && (
          <Field
            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AgencyExplanation`}
            exactAttribute
          >
            <Textarea
              {...this.props.AgencyExplanation}
              name="AgencyExplanation"
              className="legal-investigations-debarred-agency-explanation"
              onUpdate={this.updateAgencyExplanation}
              onError={this.props.onError}
              required={this.props.required}
              disabled={this.props.disabled}
              attribute={`${this.props.attribute}.AgencyExplanation`}
            />
          </Field>
          )}

        <Field
          title={i18n.t('legal.pvq.investigations.debarred.heading.date')}
          help="legal.pvq.investigations.debarred.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateRange
            {...this.props.DateRange}
            attribute={`${attribute}.DateRange`}
            name="DateRange"
            onUpdate={this.updateDateRange}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            allowPresent={false}
          />
        </Field>

        <Field
          title={i18n.t('legal.pvq.investigations.debarred.heading.explanation')}
          help="legal.pvq.investigations.debarred.help.explanation"
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            className="legal-investigations-revoked-explanation"
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
      </div>
    ) : (
      <div>
        <Field
          title={i18n.t('legal.investigations.debarred.heading.agency')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="legal-investigations-debarred-agency"
            required={this.props.required}
            attribute={`${attribute}.Agency`}
          />
        </Field>

        <Field
          title={i18n.t('legal.investigations.debarred.heading.date')}
          help="legal.investigations.debarred.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="legal-investigations-debarred-date"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('legal.investigations.debarred.heading.explanation')}
          help="legal.investigations.debarred.help.explanation"
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            name="Explanation"
            {...this.props.Explanation}
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            className="legal-investigations-debarred-explanation"
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
      </div>
    );
  }
}

DebarredItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
  AgencyExplanation: {},
  DateRange: {},
  AgencyNotApplicable: { applicable: true }
};
